import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PENSION_FAQ_CATEGORIES } from "@/content/pension-corpus/faq-defs";

const ADVICE_TH = [
  "อ่านคำแนะนำบนแบบฟอร์มของกองทุนก่อนทุกครั้ง เพราะกองทุนแต่ละแห่งกำหนดผู้รับรองที่ยอมรับไว้ต่างกัน",
  "เริ่มดำเนินการล่วงหน้า 6–8 สัปดาห์ก่อนวันครบกำหนด เพื่อเผื่อคิวนัดหมายสถานทูต คิวนิติกรณ์ และเวลาไปรษณีย์ระหว่างประเทศ",
  "ห้ามลงนามล่วงหน้าที่บ้าน การรับรองการมีชีวิตต้องลงนามต่อหน้าเจ้าหน้าที่กงสุลหรือทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร",
  "ตรวจช่วงวันที่ที่กองทุนยอมรับ หลายแบบฟอร์มกำหนดให้วันที่รับรองอยู่ภายใน 30 หรือ 60 วันก่อนวันที่กองทุนได้รับเอกสาร",
  "สะกดชื่อและนามสกุลในคำแปลให้ตรงกับหนังสือเดินทางที่ยังใช้ได้ และให้เหมือนกันทุกฉบับในชุดเดียวกัน",
  "เก็บหนังสือรับรองรายการเงินเข้าหรือ credit advice จากธนาคารไว้ เพราะใช้ประกอบการขออนุญาตอยู่ต่อได้",
  "ผู้สมัคร ABTC ควรคัดหนังสือรับรองนิติบุคคลใหม่และขอหนังสือรับรองความประพฤติล่วงหน้า เพราะทุกเขตเศรษฐกิจตรวจประวัติก่อนอนุมัติ",
  "ถ้าเปลี่ยนหนังสือเดินทางระหว่างอายุบัตร ABTC ต้องแจ้งออกบัตรใหม่ให้เลขหนังสือเดินทางตรงกัน จึงจะใช้ช่องทาง APEC lane ได้",
];

const CAUTIONS_TH = [
  "ส่งเอกสารช้ากว่ากำหนด ทำให้กองทุนระงับการจ่ายชั่วคราวและกระทบยอดเงินเข้าที่ใช้ประกอบการต่อวีซ่า",
  "ใช้แบบฟอร์มที่ร่างเองแทนแบบของกองทุน เอกสารอาจไปถึงแต่ไม่ถูกจับคู่เข้าแฟ้มเพราะไม่มีเลขอ้างอิงของผู้รับบำนาญ",
  "เว้นช่องพยานว่างไว้ทั้งที่แบบฟอร์มกำหนดพยานที่ไม่ใช่ญาติและไม่ใช่ผู้รับผลประโยชน์",
  "เข้าใจผิดว่าหนังสือรับรองสถานที่พักอาศัยของ ตม. ใช้แทนหนังสือรับรองการมีชีวิตได้ ทั้งที่เป็นเอกสารต่างประเภทกัน",
  "ยื่นคำแปลที่ยังไม่ผ่านการรับรองคำแปลของกองสัญชาติและนิติกรณ์ ทั้งที่ปลายทางกำหนดให้มีตรานิติกรณ์",
  "ไม่แจ้งกองทุนเมื่อเปลี่ยนบัญชีหรือชื่อบัญชีไม่ตรงกับชื่อผู้รับบำนาญ ทำให้การโอนถูกปฏิเสธ",
  "ทายาทถอนเงินที่กองทุนโอนมาหลังวันเสียชีวิต ซึ่งกองทุนจะเรียกคืนเกือบทุกกรณี",
  "เข้าใจว่าบัตร ABTC ให้สิทธิทำงานได้ ทั้งที่ใช้เพื่อการเดินทางเชิงธุรกิจระยะสั้นเท่านั้นและไม่ใช่ใบอนุญาตทำงาน",
];

export const Route = createFileRoute("/pension/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/pension-corpus");
    return { faqs: m.PENSION_FAQ_TH };
  },
  component: PensionFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `เอกสารบำนาญและหนังสือรับรองการมีชีวิต — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องหนังสือรับรองการมีชีวิตสำหรับผู้รับบำนาญต่างประเทศ การรับรองที่สถานทูต ทนายความผู้ทำคำรับรอง นิติกรณ์กรมการกงสุล บำนาญคู่สมรส และบัตรเดินทางนักธุรกิจเอเปก ABTC",
        path: "/pension/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/pension/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ เอกสารบำนาญและ ABTC", url: `${site}/pension/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/pension/faq")) },
      ],
    };
  },
});

function PensionFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>เอกสารบำนาญและ ABTC</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          เอกสารบำนาญต่างประเทศ หนังสือรับรองการมีชีวิต และบัตรเดินทางนักธุรกิจเอเปก
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวมคำถาม {faqs.length} ข้อเรื่องการรับรองการมีชีวิตเพื่อรักษาสิทธิบำนาญจากต่างประเทศ ลำดับการรับรองเอกสารผ่านสถานทูต
          ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร และกองสัญชาติและนิติกรณ์ กรมการกงสุล รวมถึงการขอบัตร ABTC ผ่านกรมการค้าต่างประเทศ
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกตามหมวด</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {PENSION_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/pension/faq/${c.slug}`} className="rounded-lg border bg-card p-4 hover:bg-muted">
                <span className="block font-semibold">{c.titleTh}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{c.descTh}</span>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้เจ้าหน้าที่ช่วยจัดชุดเอกสารบำนาญของท่าน</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งชื่อกองทุน ประเทศผู้จ่าย และวันครบกำหนดส่งเอกสาร ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <div className="mt-8 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/pension/faq" className="text-primary underline">Read this page in English →</a>
        </div>
        <FaqSourceNote topic="pension" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}